import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import React, { Fragment, ReactElement } from 'react';
import withStyles from 'react-jss';
import { Icon, message, Modal, Select } from 'src/Library/AntdLibComponents';

import { downloadQuotations, shareRateData } from 'src/api/discoverRates';
import { InquiryModes } from 'src/Library/Constants';
import { StylesProps, Weight } from 'src/Library/Types';
import { useGenericState } from 'src/Library/UseGenericState';
import { downloadFileData, validateEmail } from 'src/utils/utils';
const checkUrl = require('src/assets/images/check.svg');

interface ShareRateModalProps extends StylesProps<typeof styles> {
  handleModal: () => void;
  freightChargeId: string;
  originChargeId: string;
  destinationChargeId: string;
  criteriaId: string;
  criteriaRuleId: string;
  polName: string;
  podName: string;
  FFName: string;
  containerCount: number;
  cargo: { cargo: string; product: string };
  container: { type: string; size: string };
  freightValidity: string;
  shipperId: string;
  view: string;
  uuid: string;
  incoterm: string;
  movementType: string;
  shipperTeamId: string;
  originServices: string[];
  destinationServices: string[];
  mode: string;
  grossWeight: Weight;
  totalVolume: Weight;
  totalVolumetricWeight: Weight;
}

interface ShareRateModalState {
  emails: string[];
  messageBody: string;
  downloadLoader: boolean;
  sendingLoader: boolean;
  emailError: boolean;
  sendSuccess: boolean;
}

const module = 'DISCOVER_RATES';

const styles = {
  label: {
    color: '#262626',
    fontWeight: 600,
    marginBottom: '8px',
  },
  spacing: {
    margin: '12px 0',
  },
  infoMessage: {
    fontSize: '8px',
  },
  link: {
    color: '#1478F6',
    cursor: 'pointer',
  },
  font: {
    fontSize: '12px',
  },
  tags: {
    width: '100%',
  },
  textField: {
    border: '0.5px solid #D9D9D9',
    borderRadius: '4px',
    backgroundColor: '#F7F7F7',
    padding: '8px',
  },
  success: {
    textAlign: 'center',
    fontWeight: 600,
  },
};

const ShareRateModal = (props: ShareRateModalProps): ReactElement => {
  const {
    classes,
    handleModal,
    polName,
    podName,
    FFName,
    freightChargeId,
    originChargeId,
    destinationChargeId,
    criteriaId,
    criteriaRuleId,
    containerCount,
    freightValidity,
    cargo,
    container,
    shipperId,
    view,
    uuid = localStorage.getItem('searchUUID'),
    incoterm,
    movementType,
    shipperTeamId,
    originServices,
    destinationServices,
    mode,
    grossWeight,
    totalVolume,
    totalVolumetricWeight
  } = props;

  const [state, setState] = useGenericState<ShareRateModalState>({
    emails: [],
    messageBody: `
    <p>Hi,</p>
    <p class="text-tiny">Please find the quotation shared by <b>${FFName}</b> for <b>${containerCount}</b>${container?.type ? ` x <b>${container?.type} ${container?.size}</b>` : ''} full container(s) 
    to be shipped from <b>${polName} &rarr; ${podName}</b>. The rate is applicable for 
    <b>${cargo?.cargo}, ${cargo?.product}</b> with validity upto <b>${freightValidity}</b>.</p> 
    <p>Please find the attached document for detailed quotation.</p>`,
    downloadLoader: false,
    sendingLoader: false,
    emailError: false,
    sendSuccess: false,
  });

  const handleEmailChange = (data: string[]) => {
    if (data.length && validateEmail(data[data.length - 1]))
      setState({ emails: data, emailError: false });
    else if (!data.length) setState({ emails: [], emailError: true });
  };

  const changeMessageHandler = (e, editor) => setState({ messageBody: editor.getData() });

  const sendEmailHandler = async () => {
    const { messageBody, emails } = state;
    if (!emails.length) {
      setState({ emailError: true });
      return;
    }
    setState({ sendingLoader: true });
    const dataToSend: any = {
      freightChargeId,
      originChargeId,
      destinationChargeId,
      criteriaId,
      criteriaRuleId,
      containerCount,
      view,
      uuid,
      incoterm,
      movementType,
      shipperTeamId,
      originServices,
      destinationServices,
      mode,
      ...(mode !== InquiryModes.FCL && {grossWeight, totalVolume, totalVolumetricWeight})
    };
    if (shipperId !== 'nonLinkedShippers') dataToSend.shipperId = shipperId;

    const response = await shareRateData({
      sendTo: emails,
      messageBody: messageBody,
      module,
      data: dataToSend,
    });
    if (response.isSuccess) {
      setState({ sendingLoader: false, sendSuccess: true });
    } else {
      message.error(response.errorMessage);
      setState({ sendingLoader: false });
    }
  };

  const downloadQuotationhandler = async () => {
    setState({ downloadLoader: true });
    const response = await downloadQuotations(
      {
        freightChargeId,
        originChargeId,
        destinationChargeId,
        criteriaId,
        criteriaRuleId,
        containerCount,
        uuid,
        incoterm,
        movementType,
        shipperTeamId,
        originServices,
        destinationServices,
        mode,
        ...(mode !== InquiryModes.FCL && {grossWeight, totalVolume, totalVolumetricWeight})
      },
      view
    );
    if (response.isSuccess) {
      downloadFileData(response.data, response.filename || `shared-rate.pdf`, true);
      message.success('Downloaded successfully');
    } else message.error(response.errorMessage);

    setState({ downloadLoader: false });
  };

  const renderEmailInput = (): ReactElement => {
    const { emails, emailError } = state;
    return (
      <Fragment>
        <span className={classes.label}>Send To</span>
        <Select
          mode="tags"
          className={[classes.tags, classes.font, emailError && 'has-error'].join(' ')}
          placeholder="Enter email"
          onChange={handleEmailChange}
          maxTagCount={3}
          tokenSeparators={[',']}
          value={emails}
        />
        {emailError && <div style={{ color: '#f5222d' }}>Please input your E-mail!</div>}
        <span className={classes.infoMessage}>
          Add email with comma separator ( , ) and press enter
        </span>
        <div className={classes.spacing} />
      </Fragment>
    );
  };

  const renderSubject = () => (
    <Fragment>
      <span className={classes.label}>Subject</span>
      <div className={classes.textField}>
        Quotation from <b>{FFName}</b> for{' '}
        <b>
          {polName} to {podName}
        </b>
      </div>
      <div className={classes.spacing} />
    </Fragment>
  );

  const renderBody = () => {
    return (
      <Fragment>
        <span className={classes.label}>Message Body</span>
        <CKEditor editor={ClassicEditor} data={state.messageBody} onChange={changeMessageHandler} />
        <div className={classes.spacing} />
      </Fragment>
    );
  };

  return state.sendSuccess ? (
    <Modal visible footer={null} width={350} onCancel={handleModal} closable={false}>
      <div className={classes.success}>
        <img src={checkUrl} alt="success" />
        <div className={classes.spacing} />
        <p className={classes.label}>Succesfully shared rates with quotation attached.</p>
        <p className={classes.link} onClick={handleModal}>
          Okay
        </p>
      </div>
    </Modal>
  ) : (
    <Modal
      visible
      title="Sharing Via Mail"
      okText="Send"
      onCancel={handleModal}
      onOk={sendEmailHandler}
      confirmLoading={state.sendingLoader}
    >
      <div className={classes.font}>
        {renderEmailInput()}
        {renderSubject()}
        {renderBody()}
        Attachment :{' '}
        {state.downloadLoader ? (
          <span className={'loadingDots'} style={{ marginLeft: 8, color: '#51B841' }}>
            Downloading
          </span>
        ) : (
          <span
            className={[classes.link, classes.font].join(' ')}
            onClick={downloadQuotationhandler}
          >
            <Icon type="paper-clip" />
            Quotation.pdf
          </span>
        )}
      </div>
    </Modal>
  );
};

export default withStyles(styles)(ShareRateModal);
